import React, { useState, useEffect, useRef } from 'react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { Settings2, Minimize2, Maximize2 } from 'lucide-react';

// Import dashboard components
import StatsCards from '../components/dashboard/StatsCards';
import ActiveProbationCard from '../components/dashboard/ActiveProbationCard';
import { NationalityChart, GenderChart, DepartmentChart, EmploymentStatusChart, VisaCompanyChart } from '../components/dashboard/DistributionCharts';
import { SalaryCostCard, PayrollHistoryChart, PayrollComparisonWidget } from '../components/dashboard/PayrollSection';
import LeaveWarningsCard from '../components/dashboard/LeaveWarningsCard';
import { ExpiringDocumentsCard, ExpiredDocumentsCard } from '../components/dashboard/DocumentsSection';
import { CurrentLeavesCard, UpcomingLeavesTable } from '../components/dashboard/LeavesSection';
import BirthdaysCard from '../components/dashboard/BirthdaysCard';
import { SuspendedEmployeesCard, WarningLettersCard, ResignedEmployeesCard, TerminatedEmployeesCard } from '../components/dashboard/EmployeeStatusSection';
import SectionWrapper from '../components/dashboard/SectionWrapper';
import { AutoBlock, fitCols } from '../components/dashboard/AutoFit';
import PresentTodayPanel from '../components/clock/PresentTodayPanel';
import PayrollByCompanyWidget from '../components/dashboard/PayrollByCompanyWidget';
import CompanyDocumentsWidget from '../components/dashboard/CompanyDocumentsWidget';
import DctPermitAlerts from '../components/dashboard/DctPermitAlerts';
import KPIWidget from '../components/dashboard/KPIWidget';
import MissedClockoutCard from '../components/dashboard/MissedClockoutCard';
import LockedAccountsCard from '../components/dashboard/LockedAccountsCard';
import SecurityLogCard from '../components/dashboard/SecurityLogCard';
import ExitComparisonChart from '../components/dashboard/ExitComparisonChart';
import EOSBCard from '../components/dashboard/EOSBCard';
import VisaCancellationCard from '../components/dashboard/VisaCancellationCard';
import DocumentRequestsCard from '../components/dashboard/DocumentRequestsCard';
import TicketReminderCard from '../components/dashboard/TicketReminderCard';
import LeaveApprovalsCard from '../components/dashboard/LeaveApprovalsCard';
import LeaveDecisionAlertsCard from '../components/dashboard/LeaveDecisionAlertsCard';
import PayDecisionCard from '../components/dashboard/PayDecisionCard';
import DashboardCustomizeDialog from '../components/dashboard/DashboardCustomizeDialog';
import { LayoutsMenu } from '../components/dashboard/LayoutsMenu';
import { RenewalStepsAlertCard } from '../components/dashboard/RenewalStepsAlertCard';

// API imports
import {
  getDashboardStats,
  getExpiringDocuments,
  getExpiredDocuments,
  getCurrentLeaves,
  getUpcomingLeaves,
  getLeaveBalanceWarnings,
  getPayrollHistory,
  createPayrollSnapshot,
  getUpcomingBirthdays
} from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SETTINGS = { hidden_widgets: [], chart_types: {}, probation_threshold_days: null, probation_configured: false, widget_order: [], compact: false, active_layout_id: null };

// Re-orderable dashboard sections (blocks). Each block maps to one or more
// widgets; multi-widget blocks (e.g. the distribution charts) move together.
// This default order intentionally places Payroll above Active Probation.
const DEFAULT_BLOCK_ORDER = [
  'stats-cards',
  'present-today',
  'leave-approvals',
  'renewal-steps',
  'ticket-reminder',
  'missed-clockout',
  'locked-accounts',
  'security-log',
  'company-documents',
  'dct-permit',
  'charts-distribution',
  'charts-employment-visa',
  'salary-cost',
  'payroll-history',
  'payroll-comparison',
  'payroll-company',
  'active-probation',
  'kpi-leaves',
  'tables-docs',
  'birthdays',
  'employee-status',
  'eosb',
  'exit-comparison',
];

export const BLOCK_LABELS = {
  'stats-cards': 'Stats Cards',
  'present-today': 'Present Today (Live)',
  'leave-approvals': 'Leave Approvals Awaiting Decision',
  'renewal-steps': 'Visa Renewal Steps (due soon / overdue)',
  'ticket-reminder': 'Ticket Due Reminder',
  'missed-clockout': 'Missed Clock-Out Alerts',
  'locked-accounts': 'Locked Accounts',
  'security-log': 'Security Log',
  'company-documents': 'Company Documents',
  'dct-permit': 'DCT Permit Alerts',
  'charts-distribution': 'Distribution Charts (Nationality / Gender / Dept)',
  'charts-employment-visa': 'Employment & Visa Charts',
  'salary-cost': 'Monthly Salary Cost',
  'payroll-history': 'Payroll History',
  'payroll-comparison': 'Payroll Comparison',
  'payroll-company': 'Payroll by Company',
  'active-probation': 'Active Probation Staff',
  'kpi-leaves': 'KPI & Leave Warnings',
  'tables-docs': 'Leaves & Documents (incl. Upcoming Leaves)',
  'birthdays': 'Upcoming Birthdays',
  'employee-status': 'Employee Status (Suspended / Warnings / Exits)',
  'eosb': 'End of Service Benefits (EOSB)',
  'exit-comparison': 'Resignations vs Terminations (YoY)',
};

const FULL_WIDTH_BLOCKS = new Set([
  'stats-cards', 'present-today', 'leave-approvals', 'charts-distribution',
  'charts-employment-visa', 'kpi-leaves', 'tables-docs', 'employee-status', 'eosb',
]);

const WIDE_COMPACT = new Set(['salary-cost', 'payroll-company', 'payroll-comparison']);

// Full-width blocks stand alone; consecutive compact blocks share an auto-fit row.
const groupBlocks = (ids) => ids.reduce((groups, id) => {
  const full = FULL_WIDTH_BLOCKS.has(id);
  const last = groups[groups.length - 1];
  if (!full && last && !last.full) last.ids.push(id);
  else groups.push({ full, ids: [id] });
  return groups;
}, []);

// Merge saved order with the canonical default: keep valid saved ids in order,
// then append any blocks the user hasn't seen yet (new features) at the end.
const resolveBlockOrder = (saved) => {
  const valid = (saved || []).filter((id) => DEFAULT_BLOCK_ORDER.includes(id));
  const missing = DEFAULT_BLOCK_ORDER.filter((id) => !valid.includes(id));
  return [...valid, ...missing];
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiredDocs, setExpiredDocs] = useState([]);
  const [currentLeaves, setCurrentLeaves] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [employeeStatus, setEmployeeStatus] = useState(null);
  const [leaveWarnings, setLeaveWarnings] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashSettings, setDashSettings] = useState(DEFAULT_SETTINGS);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // One failing endpoint must not blank the whole dashboard.
      const settled = await Promise.allSettled([
        getDashboardStats(),
        getExpiringDocuments(90),
        getExpiredDocuments(),
        getCurrentLeaves(),
        getUpcomingLeaves(30),
        axios.get(`${API}/api/dashboard/employee-status`),
        getLeaveBalanceWarnings(),
        getPayrollHistory(12),
        getUpcomingBirthdays(30),
        axios.get(`${API}/api/dashboard/settings`)
      ]);
      settled.filter((r) => r.status === 'rejected').forEach((r) => console.error('Dashboard section failed:', r.reason));
      const [statsRes, docsRes, expiredRes, currentRes, upcomingRes, statusRes, warningsRes, payrollRes, birthdaysRes, settingsRes] =
        settled.map((r) => (r.status === 'fulfilled' ? r.value : { data: null }));

      setStats(statsRes.data);
      setExpiringDocs(docsRes.data || []);
      setExpiredDocs(expiredRes.data || []);
      setCurrentLeaves(currentRes.data || []);
      setUpcomingLeaves(upcomingRes.data || []);
      setEmployeeStatus(statusRes.data);
      setLeaveWarnings(warningsRes.data || []);
      setPayrollHistory(payrollRes.data || []);
      setUpcomingBirthdays(birthdaysRes.data || []);
      setDashSettings({ ...DEFAULT_SETTINGS, ...(settingsRes.data || {}) });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Persist a partial settings change to the backend (optimistic).
  const persistSettings = async (patch) => {
    const prev = dashSettings;
    const optimistic = { ...dashSettings, ...patch };
    if ('probation_threshold_days' in patch) {
      optimistic.probation_configured = patch.probation_threshold_days != null;
    }
    setDashSettings(optimistic);
    try {
      const res = await axios.put(`${API}/api/dashboard/settings`, patch);
      setDashSettings({ ...DEFAULT_SETTINGS, ...(res.data || {}) });
    } catch (e) {
      setDashSettings(prev);
      toast({ title: 'Error', description: 'Could not save dashboard settings', variant: 'destructive' });
    }
  };

  const isVisible = (id) => !(dashSettings.hidden_widgets || []).includes(id);

  const toggleWidget = (id) => {
    const hidden = dashSettings.hidden_widgets || [];
    const next = hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id];
    persistSettings({ hidden_widgets: next });
  };

  const chartTypeFor = (id) => (dashSettings.chart_types || {})[id] || 'bar';
  const setChartType = (id, type) => {
    persistSettings({ chart_types: { ...(dashSettings.chart_types || {}), [id]: type } });
  };

  // ─── Section ordering ──────────────────────────────────────
  const blockOrder = resolveBlockOrder(dashSettings.widget_order);
  // Mirror the resolved order in a ref so rapid consecutive clicks chain
  // correctly (each click reads the latest order even before React commits).
  const orderRef = useRef(blockOrder);
  useEffect(() => { orderRef.current = resolveBlockOrder(dashSettings.widget_order); }, [dashSettings.widget_order]);

  // Persist order without clobbering in-flight optimistic state (we do NOT
  // overwrite local state with the response — local order is the source of truth).
  const saveWidgetOrder = async (order) => {
    try {
      await axios.put(`${API}/api/dashboard/settings`, { widget_order: order });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not save section order', variant: 'destructive' });
    }
  };

  const moveBlock = (id, dir) => {
    const order = (orderRef.current || blockOrder).slice();
    const i = order.indexOf(id);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    orderRef.current = order;
    setDashSettings((prev) => ({ ...prev, widget_order: order }));
    saveWidgetOrder(order);
  };

  const applyLayout = (l, justSaved = false) => {
    const patch = { active_layout_id: l.id };
    if (!justSaved) Object.assign(patch, { widget_order: l.widget_order || [], hidden_widgets: l.hidden_widgets || [], compact: !!l.compact });
    persistSettings(patch);
    if (!justSaved) toast({ title: 'Layout applied', description: `Switched to "${l.name}".` });
  };

  const resetBlockOrder = () => {
    orderRef.current = resolveBlockOrder([]);
    setDashSettings((prev) => ({ ...prev, widget_order: [] }));
    saveWidgetOrder([]);
  };

  const handleCreateSnapshot = async () => {
    try {
      const response = await createPayrollSnapshot();
      if (response.data) {
        toast({ title: 'Success', description: 'Payroll snapshot created' });
        loadDashboardData();
      } else {
        toast({ title: 'Error', description: 'Failed to create snapshot', variant: 'destructive' });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to create snapshot';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  // Active Probation card (orderable block; threshold config preserved).
  const probationCard = isVisible('active-probation') ? (
    <ActiveProbationCard />
  ) : null;

  // Registry of renderable blocks. Multi-widget blocks return null when all
  // their child widgets are hidden so no empty grid takes up space.
  const anyVisible = (...ids) => ids.some(isVisible);
  const blockNodes = {
    'stats-cards': isVisible('stats-cards') ? <StatsCards stats={stats} /> : null,
    'present-today': isVisible('present-today') ? <PresentTodayPanel /> : null,
    'leave-approvals': isVisible('leave-approvals') ? <LeaveApprovalsCard /> : null,
    'renewal-steps': isVisible('renewal-steps') ? <RenewalStepsAlertCard /> : null,
    'ticket-reminder': isVisible('ticket-reminder') ? <TicketReminderCard compact /> : null,
    'missed-clockout': isVisible('missed-clockout') ? <MissedClockoutCard /> : null,
    'locked-accounts': isVisible('locked-accounts') ? <LockedAccountsCard /> : null,
    'security-log': isVisible('security-log') ? <SecurityLogCard /> : null,
    'company-documents': isVisible('company-documents') ? <CompanyDocumentsWidget /> : null,
    'dct-permit': isVisible('dct-permit') ? <DctPermitAlerts /> : null,
    'charts-distribution': anyVisible('nationality', 'gender', 'department') ? (
      <div className="grid gap-6" style={fitCols(380)}>
        {isVisible('nationality') && (
          <SectionWrapper title="Nationality Distribution" id="nationality">
            <NationalityChart data={stats?.nationalities} chartType={chartTypeFor('nationality')} onToggleType={(t) => setChartType('nationality', t)} />
          </SectionWrapper>
        )}
        {isVisible('gender') && (
          <SectionWrapper title="Gender Distribution" id="gender">
            <GenderChart data={stats?.gender_distribution} chartType={chartTypeFor('gender')} onToggleType={(t) => setChartType('gender', t)} />
          </SectionWrapper>
        )}
        {isVisible('department') && (
          <SectionWrapper title="Department Distribution" id="department">
            <DepartmentChart data={stats?.departments} chartType={chartTypeFor('department')} onToggleType={(t) => setChartType('department', t)} />
          </SectionWrapper>
        )}
      </div>
    ) : null,
    'charts-employment-visa': anyVisible('employment-status', 'visa-company') ? (
      <div className="grid gap-6" style={fitCols(520)}>
        {isVisible('employment-status') && (
          <SectionWrapper title="Employment Status" id="employment-status">
            <EmploymentStatusChart data={stats?.employment_status_distribution} chartType={chartTypeFor('employment-status')} onToggleType={(t) => setChartType('employment-status', t)} />
          </SectionWrapper>
        )}
        {isVisible('visa-company') && (
          <SectionWrapper title="Visa Company Distribution" id="visa-company">
            <VisaCompanyChart data={stats?.visa_company_distribution} holders={stats?.visa_holders_by_company} chartType={chartTypeFor('visa-company')} onToggleType={(t) => setChartType('visa-company', t)} />
          </SectionWrapper>
        )}
      </div>
    ) : null,
    'salary-cost': isVisible('salary-cost') ? (
      <SectionWrapper title="Monthly Salary Cost" id="salary-cost">
        <SalaryCostCard stats={stats} onCreateSnapshot={handleCreateSnapshot} />
      </SectionWrapper>
    ) : null,
    'payroll-history': isVisible('payroll-history') ? (
      <SectionWrapper title="Payroll History" id="payroll-history">
        <PayrollHistoryChart payrollHistory={payrollHistory} />
      </SectionWrapper>
    ) : null,
    'payroll-comparison': isVisible('payroll-comparison') ? (
      <SectionWrapper title="Payroll Comparison" id="payroll-comparison">
        <PayrollComparisonWidget />
      </SectionWrapper>
    ) : null,
    'payroll-company': isVisible('payroll-company') ? (
      <SectionWrapper title="Payroll by Company" id="payroll-company">
        <PayrollByCompanyWidget />
      </SectionWrapper>
    ) : null,
    'active-probation': probationCard,
    'kpi-leaves': anyVisible('kpi', 'leave-warnings') ? (
      <div className="grid gap-6" style={fitCols(480)}>
        {isVisible('kpi') && (
          <SectionWrapper title="KPI Performance" id="kpi">
            <KPIWidget />
          </SectionWrapper>
        )}
        {isVisible('leave-warnings') && (
          <SectionWrapper title="Leave Warnings" id="leave-warnings">
            <LeaveWarningsCard leaveWarnings={leaveWarnings} />
          </SectionWrapper>
        )}
      </div>
    ) : null,
    'tables-docs': anyVisible('current-leaves', 'expiring-docs', 'expired-docs', 'upcoming-leaves') ? (
      <div className="grid gap-6" style={fitCols(340)} data-testid="leaves-docs-grid">
        {isVisible('current-leaves') && (
          <SectionWrapper title="Current Leaves" id="current-leaves">
            <CurrentLeavesCard currentLeaves={currentLeaves} />
          </SectionWrapper>
        )}
        {isVisible('expiring-docs') && (
          <SectionWrapper title="Expiring Documents" id="expiring-docs">
            <ExpiringDocumentsCard expiringDocs={expiringDocs} />
          </SectionWrapper>
        )}
        {isVisible('expired-docs') && (
          <SectionWrapper title="Expired Documents" id="expired-docs">
            <ExpiredDocumentsCard expiredDocs={expiredDocs} />
          </SectionWrapper>
        )}
        {isVisible('upcoming-leaves') && (
          <SectionWrapper title="Upcoming Leaves" id="upcoming-leaves">
            <UpcomingLeavesTable upcomingLeaves={upcomingLeaves} />
          </SectionWrapper>
        )}
      </div>
    ) : null,
    'birthdays': isVisible('birthdays') ? (
      <SectionWrapper title="Upcoming Birthdays" id="birthdays">
        <BirthdaysCard upcomingBirthdays={upcomingBirthdays} />
      </SectionWrapper>
    ) : null,
    'employee-status': anyVisible('suspended', 'warnings', 'resigned', 'terminated') ? (
      <div className="grid gap-6" style={fitCols(340)}>
        {isVisible('suspended') && (
          <SectionWrapper title="Suspended Employees" id="suspended">
            <SuspendedEmployeesCard
              currentlySuspended={employeeStatus?.currently_suspended}
              upcomingSuspensions={employeeStatus?.upcoming_suspensions}
              pastSuspensions={employeeStatus?.past_suspensions}
            />
          </SectionWrapper>
        )}
        {isVisible('warnings') && (
          <SectionWrapper title="Warning Letters" id="warnings">
            <WarningLettersCard activeWarnings={employeeStatus?.active_warnings} />
          </SectionWrapper>
        )}
        {isVisible('resigned') && (
          <SectionWrapper title="Resigned Employees" id="resigned">
            <ResignedEmployeesCard resigned={employeeStatus?.resigned} />
          </SectionWrapper>
        )}
        {isVisible('terminated') && (
          <SectionWrapper title="Terminated Employees" id="terminated">
            <TerminatedEmployeesCard terminated={employeeStatus?.terminated} />
          </SectionWrapper>
        )}
      </div>
    ) : null,
    'exit-comparison': isVisible('exit-comparison') ? (
      <SectionWrapper title="Resignations vs Terminations (YoY)" id="exit-comparison">
        <ExitComparisonChart />
      </SectionWrapper>
    ) : null,
    'eosb': isVisible('eosb') ? <EOSBCard /> : null,
  };

  return (
    <div className={`min-h-screen bg-violet-50 p-4 sm:p-6 2xl:p-8 ${dashSettings.compact ? 'dash-compact' : ''}`} data-testid="dashboard">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">HR Dashboard</h1>
            <p className="text-gray-600 mt-2">Overview of your organization</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => persistSettings({ compact: !dashSettings.compact })}
              aria-pressed={!!dashSettings.compact}
              className={`inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium shadow-sm transition-colors ${dashSettings.compact ? 'border-violet-400 bg-violet-600 text-white hover:bg-violet-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-violet-300'}`}
              data-testid="compact-toggle-btn"
            >
              {dashSettings.compact ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4 text-violet-600" />}
              {dashSettings.compact ? 'Compact on' : 'Compact'}
            </button>
            <LayoutsMenu
              current={{ widget_order: blockOrder, hidden_widgets: dashSettings.hidden_widgets || [], compact: !!dashSettings.compact }}
              activeId={dashSettings.active_layout_id}
              onApply={applyLayout}
            />
            <button
              type="button"
              onClick={() => setCustomizeOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:border-violet-300 transition-colors"
              data-testid="customize-dashboard-btn"
            >
              <Settings2 className="h-4 w-4 text-violet-600" />
              Customize
            </button>
          </div>
        </div>

        <LeaveDecisionAlertsCard />
        <PayDecisionCard />
        <VisaCancellationCard />
        <DocumentRequestsCard />

        {/* Sections rendered in the user-defined order */}
        <div className="flex flex-col gap-6" data-testid="dashboard-grid">
          {groupBlocks(blockOrder.filter((id) => blockNodes[id])).map((group) => (
            group.full ? (
              <AutoBlock key={group.ids[0]} data-testid={`dash-block-${group.ids[0]}`}>
                {blockNodes[group.ids[0]]}
              </AutoBlock>
            ) : (
              <div key={group.ids.join('|')} className="flex flex-wrap gap-6 items-stretch" data-testid="dash-compact-row">
                {group.ids.map((id) => (
                  <AutoBlock key={id} className={WIDE_COMPACT.has(id) ? 'flex-[1_1_560px]' : 'flex-[1_1_400px]'} data-testid={`dash-block-${id}`}>{blockNodes[id]}</AutoBlock>
                ))}
              </div>
            )
          ))}
        </div>
      </div>

      <DashboardCustomizeDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        hiddenWidgets={dashSettings.hidden_widgets || []}
        onToggleWidget={toggleWidget}
        blockOrder={blockOrder}
        blockLabels={BLOCK_LABELS}
        onMoveBlock={moveBlock}
        onResetBlockOrder={resetBlockOrder}
      />
    </div>
  );
};

export default Dashboard;
